import UAParser from 'ua-parser-js';
import { isIOSWebView } from './check-is-social-media-browser';
import { getLogger } from './logger';

const { logError, logWarn } = getLogger({ name: 'idv2', location: 'get-device' });

export type BasicDeviceInfo = { browser: string; osName: string; type: string };
export type DeviceInfo = BasicDeviceInfo & {
  hasSupportForWebauthn: boolean;
  initialCameraPermissionState?: PermissionState;
};

/** ua-parser never emits a "desktop" type — a real desktop reports the fallback 'unknown',
 * so desktop can only be expressed as "not mobile and not tablet". */
export const isDesktopDevice = (device: Pick<BasicDeviceInfo, 'type'>): boolean =>
  device.type !== 'mobile' && device.type !== 'tablet';

// UAParser device type can have an undefined type, because
// it could get executed on the server side. We assign a default
// device, just to avoid to make a lot of ifs
const DEFAULT_DEVICE_TYPE = 'unknown';
const DEFAULT_OS_TYPE = 'unknown';
const DEFAULT_BROWSER_TYPE = 'unknown';

// Browsers that only run on mobile/tablet devices. When UA parser can't determine device type,
// detecting one of these browsers + touch capability is enough to classify as mobile.
// (Samsung Internet reports osName as 'Linux' instead of 'Android' on some devices, bypassing the OS check)
const MOBILE_ONLY_BROWSERS = new Set(['Samsung Internet', 'MIUI Browser', 'Vivo Browser', 'Huawei Browser']);

// Phones have a short-edge screen width under this threshold.
// Large phones like Samsung S24 Ultra have a short edge of ~480px and small tablets (iPad mini) start at ~744px.
const MOBILE_SCREEN_SHORT_EDGE_MAX = 600;

export const getCameraPermissionState = async (): Promise<PermissionState> => {
  if (!navigator) {
    return Promise.resolve('denied');
  }
  if (!navigator.permissions) {
    return Promise.resolve('denied');
  }
  if (typeof navigator.permissions.query !== 'function') {
    return Promise.resolve('denied');
  }

  try {
    const res = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return res.state;
  } catch (_err) {
    return 'denied';
  }
};

// Return device without webauthn status for clients that don't care
export const getBasicDevice = () => {
  const uaParser = new UAParser();
  const device = uaParser.getDevice();
  const os = uaParser.getOS();
  const browser = uaParser.getBrowser();

  let type = device.type || DEFAULT_DEVICE_TYPE;
  let fallbackUsed: string | undefined;

  // ua-parser-js can fail to determine device type for certain mobile browsers,
  // so apply a series of fallbacks to avoid misclassifying mobile devices as desktop.
  if (type === DEFAULT_DEVICE_TYPE && typeof navigator !== 'undefined') {
    const isMobileOS = os.name === 'iOS' || os.name === 'Android';
    const isMobileOnlyBrowser = MOBILE_ONLY_BROWSERS.has(browser.name ?? '');
    const hasTouch = navigator.maxTouchPoints > 0;

    // Known mobile OS or mobile-only browser with touch capability
    if ((isMobileOS || isMobileOnlyBrowser) && hasTouch) {
      type = 'mobile';
      fallbackUsed = 'os-or-browser';
    }
    // User-Agent Client Hints API (only on Chromium browsers): direct mobile indicator independent of the UA string format
    else if ('userAgentData' in navigator && (navigator.userAgentData as { mobile?: boolean })?.mobile === true) {
      type = 'mobile';
      fallbackUsed = 'client-hints';
    }
    // Touch device with narrow screen to catch browsers sending desktop-like UA strings (eg Chrome "Request Desktop Site", Samsung DeX, certain WebViews)
    else if (hasTouch && typeof window !== 'undefined') {
      const sw = window.screen?.width ?? 0;
      const sh = window.screen?.height ?? 0;
      const shortEdge = Math.min(sw, sh); // Math.min to be orientation-independent
      if (shortEdge > 0 && shortEdge <= MOBILE_SCREEN_SHORT_EDGE_MAX) {
        type = 'mobile';
        fallbackUsed = 'screen-size';
      }
    }

    if (fallbackUsed) {
      logWarn(`Device classified as mobile via fallback: ${fallbackUsed}`, undefined, {
        uaDeviceType: device.type,
        osName: os.name,
        browserName: browser.name,
        maxTouchPoints: navigator.maxTouchPoints,
        screenWidth: typeof window !== 'undefined' ? window.screen?.width : undefined,
        screenHeight: typeof window !== 'undefined' ? window.screen?.height : undefined,
      });
    }
  }

  const info: BasicDeviceInfo = {
    browser: browser.name || DEFAULT_BROWSER_TYPE,
    osName: os.name || DEFAULT_OS_TYPE,
    type,
  };
  return info;
};

export const checkDeviceInfo = async () => {
  let hasSupportForWebauthn = false;
  if (typeof window !== 'undefined' && window.PublicKeyCredential) {
    try {
      hasSupportForWebauthn = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      logError('Error checking for WebAuthn support', error);
    }
  }
  const basicInfo = getBasicDevice();

  // iOS 16+ natively supports passkeys, but isUserVerifyingPlatformAuthenticatorAvailable()
  // can incorrectly return false in non-Safari browsers (Chrome, Firefox, etc.) since they all
  // use WebKit under the hood and don't always surface the platform authenticator correctly.
  // An app's embedded WKWebView is the exception: its false is real, since only apps holding
  // our webcredentials entitlement can use onefootprint.com passkeys.
  if (!hasSupportForWebauthn && basicInfo.osName === 'iOS' && !isIOSWebView(navigator.userAgent)) {
    const uaParser = new UAParser();
    const osVersion = parseFloat(uaParser.getOS().version || '0');
    if (osVersion >= 16) {
      hasSupportForWebauthn = true;
    }
  }

  return { hasSupportForWebauthn, ...basicInfo };
};

const getDevice = async () => {
  const [deviceInfo, initialCameraPermissionState] = await Promise.all([checkDeviceInfo(), getCameraPermissionState()]);
  return { ...deviceInfo, initialCameraPermissionState };
};

export default getDevice;
